import InfoTip from "../../InfoTip";
import "./drawerStats.css";
import { grammarBenchmark, useGrammarBenchmarks } from "./useGrammarBenchmarks";

/** Same family as destination stack: teal, blue, terracotta. */
const BAR_COLORS = ["#00515b", "#6283c4", "#c46a3a"];

function fmt(value, digits) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

function place(value, min, max) {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (max === min) return 50;
  return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

function scopeLine(headline, bars, year) {
  const bench = headline?.bench || bars.find((item) => item.bench)?.bench;
  const label = headline?.bench ? headline.label : bars.find((item) => item.bench)?.label;
  if (!bench) return "Grammar average did not load.";
  const base = bench.sameYear
    ? `Mean of ${bench.count} grammar schools with a ${year} ${label} figure.`
    : `Mean of ${bench.count} grammar schools, each using its latest stored ${label} figure.`;
  const mixed = bars.some((item) => item.bench && item.bench.count !== bench.count);
  return mixed ? `${base} Each grade bar uses the schools that published that share.` : base;
}

function Stand({ label, school, bench, digits, hint }) {
  if (!Number.isFinite(Number(school)) || !bench) return null;
  const schoolAt = place(Number(school), bench.min, bench.max);
  const meanAt = place(bench.mean, bench.min, bench.max);
  return (
    <div className="ds-stand">
      <div className="ds-stand-head">
        <span className="v2-info-tip--label">
          {label}
          {hint ? <InfoTip text={hint} /> : null}
        </span>
        <strong>{fmt(school, digits)}</strong>
      </div>
      <div
        className="ds-range"
        role="img"
        aria-label={`${label}: this school ${fmt(school, digits)}, grammar mean ${fmt(bench.mean, digits)}, from ${fmt(bench.min, digits)} to ${fmt(bench.max, digits)}`}
      >
        <div className="ds-range-track">
          <span className="ds-range-mean" style={{ left: `${meanAt}%` }} />
          <span className="ds-range-school" style={{ left: `${schoolAt}%` }} />
        </div>
      </div>
      <div className="ds-range-scale">
        <span>{fmt(bench.min, digits)}</span>
        <span>Mean {fmt(bench.mean, digits)}</span>
        <span>{fmt(bench.max, digits)}</span>
      </div>
    </div>
  );
}

function Bars({ items }) {
  const visible = items.filter((item) => Number.isFinite(Number(item.school)));
  if (!visible.length) return null;
  return (
    <div className="ds-bars">
      {visible.map((item, index) => {
        const school = Number(item.school);
        const mean = item.bench ? Number(item.bench.mean) : null;
        const color = item.color || BAR_COLORS[index % BAR_COLORS.length];
        return (
          <div className="ds-bar" key={item.label}>
            <div className="ds-bar-label">
              <span className="ds-bar-name">
                <i className="ds-swatch" style={{ background: color }} aria-hidden />
                {item.label}
              </span>
              <strong>{fmt(school, 1)}%</strong>
            </div>
            <div
              className="ds-bar-track"
              role="img"
              aria-label={
                Number.isFinite(mean)
                  ? `${item.label}: this school ${fmt(school, 1)}%, grammar mean ${fmt(mean, 1)}%`
                  : `${item.label}: this school ${fmt(school, 1)}%`
              }
            >
              <span
                className="ds-bar-fill"
                style={{ width: `${Math.min(100, Math.max(0, school))}%`, background: color }}
              />
              {Number.isFinite(mean) ? (
                <span className="ds-bar-mean" style={{ left: `${Math.min(100, Math.max(0, mean))}%` }} />
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ResultPicture({ year, yearKey, headline, bars }) {
  const { rows, loading, error } = useGrammarBenchmarks(true);
  const headlineBench = grammarBenchmark(rows, yearKey, headline.valueKey, year);
  const barItems = bars.map((bar) => ({
    ...bar,
    bench: grammarBenchmark(rows, yearKey, bar.valueKey, year),
  }));
  const headlineItem = { ...headline, bench: headlineBench };
  const scopeBench = headlineBench || barItems.find((item) => item.bench)?.bench || null;

  return (
    <div className="ds-picture">
      <div className="ds-legend">
        <span><i className="ds-swatch ds-swatch--school" /> This school</span>
        <span><i className="ds-swatch ds-swatch--mean" /> Grammar mean</span>
      </div>
      {loading ? <p className="ds-hint">Loading the grammar mean…</p> : null}
      {error ? <p className="ds-hint">The grammar mean did not load. The school figure is still shown.</p> : null}
      <Stand
        label={headline.label}
        school={headline.school}
        bench={headlineBench}
        digits={headline.digits}
        hint={headline.hint}
      />
      <Bars items={barItems} />
      {scopeBench ? (
        <p className="ds-hint">
          <span className="v2-info-tip--label">
            Grammar mean
            <InfoTip text={scopeLine(headlineItem, barItems, year)} />
          </span>
        </p>
      ) : null}
    </div>
  );
}
